import { axios, transformAxiosError } from "../utils/axios.create";
import { OrganizationUnit } from "../domain/organizations.interface";

export type FindAllOrganizationsPayload = {
  search?: string;
  status?: string;
  pageNumber: number;
  pageSize: number;
  sortField: string;
  sortOrder: number;
};

export type OrganizationPayload = {
  name: string;
  description: string;
  status: "ACTIVE" | "INACTIVE";
  orgUnitLabelId?: string[];
  createdBy?: string;
  updatedBy?: string;
};

export type OrganizationsListResponse = {
  list: OrganizationUnit[];
  totalCount: number;
};

const findByIds = async ({
  ids,
  status,
}: {
  ids: string[];
  status: string;
}): Promise<OrganizationUnit[]> => {
  const statusQuery = new URLSearchParams({
    status: status,
    _id: ids.join(","),
  }).toString();

  return axios
    .get(`/organizations?${statusQuery}`)
    .then((res) => {
      const body = res.data.body;
      return (body?.list as OrganizationUnit[]) || [];
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const findAll = async ({
  search,
  status,
  pageNumber,
  pageSize,
  sortField,
  sortOrder,
}: FindAllOrganizationsPayload): Promise<OrganizationsListResponse> => {
  const queryParams = new URLSearchParams({
    pageNumber: String(pageNumber + 1),
    pageSize: String(pageSize),
    sortField,
    sortOrder: sortOrder === 1 ? "asc" : "desc",
  });

  if (search) {
    queryParams.append("search", search);
  }

  if (status) {
    queryParams.append("status", status);
  }

  return axios
    .get(`/organizations?${queryParams.toString()}`)
    .then((res) => {
      const body = res.data.body;
      return {
        list: body.list || [],
        totalCount: body.totalCount || 0,
      };
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const findOne = async (id: string): Promise<OrganizationUnit> => {
  return axios
    .get(`/organizations/find/${id}`)
    .then((res) => {
      return res.data.body as OrganizationUnit;
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const create = async (data: OrganizationPayload): Promise<OrganizationUnit> => {
  return axios
    .post("/organizations", data)
    .then((res) => {
      return res.data.body as OrganizationUnit;
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const update = async ({
  id,
  data,
}: {
  id: string;
  data: OrganizationPayload;
}): Promise<OrganizationUnit> => {
  return axios
    .put(`/organizations/${id}`, data)
    .then((res) => {
      return res.data.body as OrganizationUnit;
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const archive = async (id: string): Promise<void> => {
  return axios
    .patch(`/organizations/archive/${id}`)
    .then(() => {
      return;
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

export const OrganizationService = {
  findByIds,
  findAll,
  findOne,
  create,
  update,
  archive,
};
